import React from "react";
import DOMPurify from "dompurify";
import {
  FaCheck,
  FaEye,
  FaImage,
  FaPen,
  FaTimes,
  FaTrashAlt,
  FaUserSlash,
} from "react-icons/fa";

import { getMediaUrl } from "../utils/disciplinesUtils";

function IconButton({ title, icon: Icon, onClick, tone = "slate" }) {
  const tones = {
    slate:
      "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-950 dark:border-slate-700 dark:text-slate-300 dark:hover:text-white",
    blue:
      "border-blue-100 text-blue-600 hover:border-blue-200 hover:bg-blue-50 dark:border-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/10",
    orange:
      "border-orange-100 text-orange-600 hover:border-orange-200 hover:bg-orange-50 dark:border-orange-500/20 dark:text-orange-300 dark:hover:bg-orange-500/10",
    rose:
      "border-rose-100 text-rose-600 hover:border-rose-200 hover:bg-rose-50 dark:border-rose-500/20 dark:text-rose-300 dark:hover:bg-rose-500/10",
  };

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`grid h-10 w-10 place-items-center rounded-xl border bg-white text-sm transition dark:bg-slate-950 ${tones[tone]}`}
    >
      <Icon />
    </button>
  );
}

function DisciplineSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-48 animate-pulse bg-slate-100 dark:bg-slate-800" />
      <div className="p-5">
        <div className="h-5 w-32 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
        <div className="mt-4 h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
      </div>
    </div>
  );
}

export default function DisciplinesGrid({
  loading,
  disciplines = [],
  deleteCandidateId,
  onDetails,
  onEdit,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
}) {
  if (loading) {
    return (
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <DisciplineSkeleton key={index} />
        ))}
      </section>
    );
  }

  if (!disciplines.length) {
    return (
      <section className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
          <FaUserSlash className="text-2xl" />
        </div>

        <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
          Aucune discipline trouvée
        </h3>

        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Modifiez votre recherche ou créez une nouvelle discipline.
        </p>
      </section>
    );
  }

  return (
    <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
      {disciplines.map((discipline) => {
        const imageUrl = getMediaUrl(discipline.image_discipline);
        const isDeleting = deleteCandidateId === discipline.id;

        return (
          <article
            key={discipline.id}
            className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm shadow-slate-200/60 transition hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/80 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none"
          >
            <div className="relative h-56 overflow-hidden bg-slate-100 dark:bg-slate-800">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={discipline.nom_discipline}
                  className="h-full w-full object-cover transition duration-500 hover:scale-105"
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-slate-400">
                  <FaImage className="text-4xl" />
                  <p className="mt-3 text-sm font-medium">
                    Aucune image renseignée
                  </p>
                </div>
              )}

              <span className="absolute left-4 top-4 rounded-full bg-slate-950/80 px-3 py-1 text-xs font-bold text-white backdrop-blur">
                Ordre {discipline.ordre_discipline ?? "—"}
              </span>
            </div>

            <div className="p-5">
              <h3 className="text-xl font-bold text-slate-950 dark:text-white">
                {discipline.nom_discipline}
              </h3>

              <p className="mt-2 text-sm font-medium text-orange-600 dark:text-orange-300">
                {discipline.description_discipline ||
                  "Description non renseignée"}
              </p>

              {discipline.contenu_discipline ? (
                <div
                  className="prose prose-sm mt-3 max-h-[96px] max-w-none overflow-hidden text-slate-500 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 dark:prose-invert dark:text-slate-400"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      discipline.contenu_discipline
                    ),
                  }}
                />
              ) : (
                <p className="mt-3 min-h-[72px] text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Aucun contenu détaillé pour cette discipline.
                </p>
              )}

              <div className="mt-5 flex flex-wrap justify-end gap-2">
                {isDeleting ? (
                  <>
                    <button
                      type="button"
                      onClick={() => onConfirmDelete(discipline.id)}
                      className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
                    >
                      <FaCheck />
                      Confirmer
                    </button>

                    <button
                      type="button"
                      onClick={onCancelDelete}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      <FaTimes />
                      Annuler
                    </button>
                  </>
                ) : (
                  <>
                    <IconButton
                      title="Voir le détail"
                      icon={FaEye}
                      tone="blue"
                      onClick={() => onDetails(discipline)}
                    />

                    <IconButton
                      title="Modifier"
                      icon={FaPen}
                      tone="orange"
                      onClick={() => onEdit(discipline)}
                    />

                    <IconButton
                      title="Supprimer"
                      icon={FaTrashAlt}
                      tone="rose"
                      onClick={() => onAskDelete(discipline.id)}
                    />
                  </>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}